import React from "react";
import { useLocation } from "react-router-dom";
import { getBlogPost } from "@/lib/blogContent";
import { useLang } from "@/lib/LanguageContext";
import { runtimeConfig } from "@/lib/runtimeConfig";

const SUPPORTED_LANGUAGES = ["en", "fr", "es", "ar"];
const DEFAULT_LANGUAGE = "en";
const OG_IMAGE = "/og-image.png";
const LAST_MODIFIED = "2026-05-20";

const LOCALES = {
  en: "en_US",
  fr: "fr_CA",
  es: "es_ES",
  ar: "ar_AR",
};

const SEO_COPY = {
  en: {
    homeTitle: "Aevo - Private event photo gallery, QR uploads and event tools",
    homeDescription:
      "Create a private event photo gallery with QR guest uploads, themed photo styles, AI moderation, QR Print Studio, Live Wall, Best Of recaps and optional Stripe event funds.",
    blogTitle: "Event photo sharing guides | Aevo",
    blogDescription:
      "Guides for collecting event photos, protecting private galleries, and creating smoother guest photo-sharing experiences.",
    faqTitle: "Aevo FAQ - Private event photo galleries",
    faqDescription:
      "Answers about Aevo event galleries, QR code guest uploads, privacy, plans, themed photo styles, AI moderation, QR Print Studio, Live Wall, Best Of, downloads, and event funds.",
    privacyTitle: "Privacy Policy | Aevo",
    privacyDescription:
      "Learn how Aevo handles private event galleries, guest photos, account data, moderation data, and retention.",
    termsTitle: "Terms of Service | Aevo",
    termsDescription:
      "Review the terms for using Aevo to create private event photo galleries, invite guests, upload photos, and manage event plans.",
    rulesTitle: "Community Rules | Aevo",
    rulesDescription:
      "Read the community rules for safe, respectful, private event photo sharing on Aevo.",
    statusTitle: "Aevo Status & Support",
    statusDescription:
      "Check Aevo service status for public pages, QR galleries, guest uploads, Stripe payments, and support notifications.",
    joinTitle: "Join an Aevo event",
    joinDescription: "Join a private Aevo event gallery with an access code or QR code.",
    faqItems: [
      ["Do guests need an app?", "No. Guests can scan the QR code and upload photos from their mobile browser."],
      ["Are galleries private?", "Yes. Event galleries use private access through a QR code or event code."],
      ["Can organizers download photos?", "Yes. Paid plans include gallery exports so organizers can keep the event archive."],
      ["How does the event fund work?", "Organizers can enable an optional private fund and receive payouts through Stripe Connect."],
      ["What premium tools are included?", "Aevo includes QR Print Studio, themed photo styles, Best Of recaps, Live Wall displays, AI moderation, downloads, and organizer tools depending on the selected plan."],
    ],
  },
  fr: {
    homeTitle: "Aevo - Galerie photo evenement, QR invite et outils premium",
    homeDescription:
      "Creez une galerie photo privee avec QR code invite, styles photo thematiques, moderation IA, QR Print Studio, Live Wall, Best Of et cagnotte evenement via Stripe.",
    blogTitle: "Guides de partage photo evenementiel | Aevo",
    blogDescription:
      "Guides pour collecter les photos d'evenement, proteger les galeries privees et offrir une experience simple aux invites.",
    faqTitle: "FAQ Aevo - Galeries photo privees pour evenements",
    faqDescription:
      "Reponses sur les galeries Aevo, QR code, uploads invites, confidentialite, forfaits, styles photo thematiques, moderation IA, QR Print Studio, Live Wall, Best Of, telechargements et cagnotte.",
    privacyTitle: "Politique de confidentialite | Aevo",
    privacyDescription:
      "Decouvrez comment Aevo gere les galeries privees, photos invites, donnees de compte, moderation et duree de conservation.",
    termsTitle: "Conditions d'utilisation | Aevo",
    termsDescription:
      "Consultez les conditions pour creer une galerie photo privee, inviter des participants, uploader des photos et gerer un forfait evenement.",
    rulesTitle: "Regles de communaute | Aevo",
    rulesDescription:
      "Consultez les regles pour un partage photo evenementiel prive, securise et respectueux sur Aevo.",
    statusTitle: "Statut et support Aevo",
    statusDescription:
      "Verifiez le statut Aevo pour le site public, les galeries QR, les uploads invites, les paiements Stripe et les notifications support.",
    joinTitle: "Rejoindre un evenement Aevo",
    joinDescription: "Rejoignez une galerie privee Aevo avec un code d'acces ou un QR code.",
    faqItems: [
      ["Les invites ont-ils besoin d'une application ?", "Non. Ils scannent le QR code et ajoutent leurs photos depuis le navigateur mobile."],
      ["Les galeries sont-elles privees ?", "Oui. Chaque galerie est accessible avec un QR code ou un code d'evenement."],
      ["L'organisateur peut-il telecharger les photos ?", "Oui. Les forfaits payants incluent l'export de la galerie."],
      ["Comment fonctionne la cagnotte ?", "L'organisateur peut activer une cagnotte privee optionnelle et recevoir le versement via Stripe Connect."],
      ["Quels outils premium sont inclus ?", "Aevo inclut QR Print Studio, styles photo thematiques, Best Of, Live Wall, moderation IA, exports et outils organisateur selon le forfait choisi."],
    ],
  },
  es: {
    homeTitle: "Aevo - Galeria privada de eventos, QR e herramientas premium",
    homeDescription:
      "Crea una galeria privada con QR para invitados, estilos de foto tematicos, moderacion IA, QR Print Studio, Live Wall, Best Of y fondo de evento via Stripe.",
    blogTitle: "Guias de fotos para eventos | Aevo",
    blogDescription:
      "Guias para recopilar fotos de eventos, proteger galerias privadas y simplificar la experiencia de los invitados.",
    faqTitle: "FAQ Aevo - Galerias privadas de eventos",
    faqDescription:
      "Respuestas sobre galerias Aevo, codigos QR, uploads de invitados, privacidad, planes, estilos de foto tematicos, moderacion IA, QR Print Studio, Live Wall, Best Of, descargas y fondos de evento.",
    privacyTitle: "Politica de privacidad | Aevo",
    privacyDescription:
      "Conoce como Aevo gestiona galerias privadas, fotos de invitados, datos de cuenta, moderacion y retencion.",
    termsTitle: "Terminos de servicio | Aevo",
    termsDescription:
      "Consulta los terminos para crear galerias privadas, invitar participantes, subir fotos y gestionar planes de evento.",
    rulesTitle: "Reglas de comunidad | Aevo",
    rulesDescription:
      "Lee las reglas para compartir fotos de eventos de forma privada, segura y respetuosa en Aevo.",
    statusTitle: "Estado y soporte de Aevo",
    statusDescription:
      "Consulta el estado de Aevo para el sitio publico, galerias QR, cargas de invitados, pagos Stripe y notificaciones de soporte.",
    joinTitle: "Unirse a un evento Aevo",
    joinDescription: "Unete a una galeria privada de Aevo con un codigo de acceso o codigo QR.",
    faqItems: [
      ["Los invitados necesitan una app?", "No. Pueden escanear el QR y subir fotos desde el navegador movil."],
      ["Las galerias son privadas?", "Si. Cada galeria usa acceso privado con QR o codigo de evento."],
      ["El organizador puede descargar las fotos?", "Si. Los planes pagos incluyen exportacion de la galeria."],
      ["Como funciona el fondo del evento?", "El organizador puede activar un fondo privado opcional y recibir el pago via Stripe Connect."],
      ["Que herramientas premium incluye?", "Aevo incluye QR Print Studio, estilos de foto tematicos, Best Of, Live Wall, moderacion IA, exportaciones y herramientas de organizador segun el plan."],
    ],
  },
  ar: {
    homeTitle: "Aevo - معارض صور خاصة للفعاليات عبر رمز QR",
    homeDescription:
      "أنشئ معرض صور خاصاً لفعاليتك عبر رمز QR. يرفع الضيوف الصور من دون تطبيق، ويدير المنظم الألبوم المباشر والصندوق الاختياري.",
    blogTitle: "أدلة مشاركة صور الفعاليات | Aevo",
    blogDescription:
      "أدلة لجمع صور الفعاليات، حماية المعارض الخاصة، وتسهيل تجربة مشاركة الصور للضيوف.",
    faqTitle: "الأسئلة الشائعة | Aevo",
    faqDescription:
      "إجابات حول معارض Aevo الخاصة، رموز QR، رفع صور الضيوف، الخصوصية، الخطط، المراجعة، التنزيلات وصندوق الفعالية.",
    privacyTitle: "سياسة الخصوصية | Aevo",
    privacyDescription:
      "تعرف على كيفية تعامل Aevo مع معارض الفعاليات الخاصة، صور الضيوف، بيانات الحساب، المراجعة ومدة الحفظ.",
    termsTitle: "شروط الخدمة | Aevo",
    termsDescription:
      "راجع شروط استخدام Aevo لإنشاء معارض صور خاصة، دعوة الضيوف، رفع الصور وإدارة خطط الفعاليات.",
    rulesTitle: "قواعد المجتمع | Aevo",
    rulesDescription:
      "اقرأ القواعد الخاصة بمشاركة صور الفعاليات بشكل خاص وآمن ومحترم على Aevo.",
    statusTitle: "حالة ودعم Aevo",
    statusDescription:
      "تحقق من حالة خدمات Aevo للصفحات العامة ومعارض QR ورفع صور الضيوف ومدفوعات Stripe وتنبيهات الدعم.",
    joinTitle: "الانضمام إلى فعالية Aevo",
    joinDescription: "انضم إلى معرض Aevo خاص باستخدام رمز وصول أو رمز QR.",
    faqItems: [
      ["هل يحتاج الضيوف إلى تطبيق؟", "لا. يمكنهم مسح رمز QR ورفع الصور من متصفح الهاتف."],
      ["هل المعارض خاصة؟", "نعم. يتم الوصول إلى كل معرض عبر رمز QR أو رمز فعالية."],
      ["هل يستطيع المنظم تنزيل الصور؟", "نعم. تتضمن الخطط المدفوعة تصدير المعرض."],
      ["كيف يعمل صندوق الفعالية؟", "يمكن للمنظم تفعيل صندوق خاص اختياري واستلام التحويل عبر Stripe Connect."],
    ],
  },
};

const PRODUCT_FEATURES = {
  en: [
    "Private event photo gallery",
    "QR code guest uploads without an app",
    "QR Print Studio for posters, table cards and social stories",
    "Themed photo styles for event uploads",
    "Live Wall premium slideshow",
    "Best Of visual recaps",
    "AI photo moderation",
    "Optional Stripe event fund",
  ],
  fr: [
    "Galerie photo evenement privee",
    "Uploads invites par QR code sans application",
    "QR Print Studio pour affiches, cartes de table et stories",
    "Styles photo thematiques pour les uploads",
    "Live Wall premium",
    "Recaps visuels Best Of",
    "Moderation photo par IA",
    "Cagnotte evenement optionnelle via Stripe",
  ],
  es: [
    "Galeria privada para eventos",
    "Subida de fotos con QR sin app",
    "QR Print Studio para posters, tarjetas de mesa y stories",
    "Estilos de foto tematicos para el evento",
    "Live Wall premium",
    "Resumenes visuales Best Of",
    "Moderacion de fotos con IA",
    "Fondo de evento opcional via Stripe",
  ],
  ar: [
    "Private event photo gallery",
    "QR guest uploads without an app",
    "QR Print Studio",
    "Themed photo styles",
    "Live Wall premium slideshow",
    "Best Of visual recaps",
    "AI photo moderation",
    "Optional Stripe event fund",
  ],
};

function normalizePath(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "");
}

function cleanSiteUrl() {
  return String(runtimeConfig.siteUrl || "https://aevo.live").replace(/\/+$/, "");
}

function urlFor(pathname = "/", lang = DEFAULT_LANGUAGE) {
  const url = new URL(normalizePath(pathname), `${cleanSiteUrl()}/`);
  if (lang && lang !== DEFAULT_LANGUAGE) url.searchParams.set("lang", lang);
  return url.toString();
}

function imageUrl(path = OG_IMAGE) {
  return new URL(path, `${cleanSiteUrl()}/`).toString();
}

function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertLink(rel, attributes) {
  const selector = attributes.hreflang
    ? `link[rel="${rel}"][hreflang="${attributes.hreflang}"]`
    : `link[rel="${rel}"]`;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
}

function setJsonLd(id, data) {
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement("script");
    element.type = "application/ld+json";
    element.id = id;
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

function pageSchema(page, lang, url) {
  const appName = runtimeConfig.appName || "Aevo";
  const logo = imageUrl("/icon-512.png");
  const featureList = PRODUCT_FEATURES[lang] || PRODUCT_FEATURES[DEFAULT_LANGUAGE];

  if (page.kind === "home") {
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${cleanSiteUrl()}/#organization`,
          name: appName,
          url: cleanSiteUrl(),
          logo,
          contactPoint: {
            "@type": "ContactPoint",
            email: runtimeConfig.supportEmail,
            contactType: "customer support",
          },
        },
        {
          "@type": "WebSite",
          "@id": `${cleanSiteUrl()}/#website`,
          name: appName,
          url: cleanSiteUrl(),
          inLanguage: lang,
        },
        {
          "@type": "SoftwareApplication",
          name: appName,
          applicationCategory: "EventApplication",
          operatingSystem: "Web",
          url,
          inLanguage: lang,
          description: page.description,
          featureList,
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "USD",
            lowPrice: "0",
            highPrice: "149",
          },
        },
      ],
    };
  }

  if (page.kind === "faq") {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqItems.map(([question, answer]) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
        },
      })),
    };
  }

  if (page.kind === "article") {
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: page.articleTitle,
      description: page.description,
      url,
      image: imageUrl(),
      dateModified: LAST_MODIFIED,
      author: {
        "@type": "Organization",
        name: appName,
      },
      publisher: {
        "@type": "Organization",
        name: appName,
        logo: {
          "@type": "ImageObject",
          url: logo,
        },
      },
      inLanguage: lang,
    };
  }

  if (page.kind === "blog") {
    return {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: page.title,
      description: page.description,
      url,
      inLanguage: lang,
    };
  }

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.title,
    description: page.description,
    url,
    inLanguage: lang,
  };
}

function resolvePage(pathname, lang) {
  const path = normalizePath(pathname);
  const copy = SEO_COPY[lang] || SEO_COPY[DEFAULT_LANGUAGE];

  if (path === "/" || path === "/welcome") {
    return {
      kind: "home",
      path: "/",
      title: copy.homeTitle,
      description: copy.homeDescription,
      noindex: path === "/welcome",
      faqItems: copy.faqItems,
    };
  }

  if (path === "/blog") {
    return {
      kind: "blog",
      path,
      title: copy.blogTitle,
      description: copy.blogDescription,
      noindex: false,
    };
  }

  if (path.startsWith("/blog/")) {
    const slug = path.split("/").filter(Boolean)[1];
    const post = getBlogPost(slug, lang);
    if (!post) {
      return {
        kind: "missing",
        path,
        title: `Page not found | ${runtimeConfig.appName}`,
        description: "This page does not exist.",
        noindex: true,
      };
    }
    return {
      kind: "article",
      path,
      title: `${post.title} | Aevo`,
      articleTitle: post.title,
      description: post.excerpt,
      noindex: false,
    };
  }

  const staticPages = {
    "/faq": { kind: "faq", title: copy.faqTitle, description: copy.faqDescription, faqItems: copy.faqItems },
    "/privacy": { kind: "webpage", title: copy.privacyTitle, description: copy.privacyDescription },
    "/terms": { kind: "webpage", title: copy.termsTitle, description: copy.termsDescription },
    "/rules": { kind: "webpage", title: copy.rulesTitle, description: copy.rulesDescription },
    "/status": { kind: "webpage", title: copy.statusTitle, description: copy.statusDescription },
    "/join": { kind: "utility", title: copy.joinTitle, description: copy.joinDescription, noindex: true },
  };

  if (staticPages[path]) {
    return {
      path,
      noindex: false,
      ...staticPages[path],
    };
  }

  return {
    kind: "private",
    path,
    title: `${runtimeConfig.appName} app`,
    description: "Private Aevo application page.",
    noindex: true,
  };
}

export default function SEOManager() {
  const location = useLocation();
  const { lang } = useLang();
  const activeLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;

  React.useEffect(() => {
    const page = resolvePage(location.pathname, activeLang);
    const canonicalUrl = urlFor(page.path, activeLang);
    const robots = page.noindex
      ? "noindex,nofollow"
      : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";

    document.title = page.title;
    upsertMeta("name", "description", page.description);
    upsertMeta("name", "robots", robots);
    upsertMeta("name", "application-name", runtimeConfig.appName || "Aevo");
    upsertMeta("name", "theme-color", "#111827");
    upsertMeta("property", "og:type", page.kind === "article" ? "article" : "website");
    upsertMeta("property", "og:locale", LOCALES[activeLang] || LOCALES.en);
    upsertMeta("property", "og:site_name", runtimeConfig.appName || "Aevo");
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:title", page.title);
    upsertMeta("property", "og:description", page.description);
    upsertMeta("property", "og:image", imageUrl());
    upsertMeta("property", "og:image:type", "image/png");
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", page.title);
    upsertMeta("name", "twitter:description", page.description);
    upsertMeta("name", "twitter:image", imageUrl());
    upsertLink("canonical", { href: canonicalUrl });

    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => node.remove());
    document.head.querySelectorAll('meta[property="og:locale:alternate"]').forEach((node) => node.remove());

    if (!page.noindex) {
      SUPPORTED_LANGUAGES.forEach((language) => {
        upsertLink("alternate", {
          hreflang: language,
          href: urlFor(page.path, language),
        });
        if (language !== activeLang) {
          const alternate = document.createElement("meta");
          alternate.setAttribute("property", "og:locale:alternate");
          alternate.setAttribute("content", LOCALES[language] || LOCALES.en);
          document.head.appendChild(alternate);
        }
      });
      upsertLink("alternate", {
        hreflang: "x-default",
        href: urlFor(page.path, DEFAULT_LANGUAGE),
      });
    }

    setJsonLd("aevo-seo-schema", pageSchema(page, activeLang, canonicalUrl));
  }, [activeLang, location.pathname]);

  return null;
}
